import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ConfirmDialog,
  ConfirmDeleteBaseProps,
} from '../ui/dialog/ConfirmDialog';
import { useAppContext } from '../../contexts/AppContext';
import { ViewOrderDto } from '../../lib/api/orders/dto/view-order.dto';
import { getErrorMessage } from '../../lib/util/misc-utils';
import {
  CartQueryKeys,
  setupFromExistingOrder,
} from '../../lib/api/carts/cart.service';

interface Props extends ConfirmDeleteBaseProps {
  order: ViewOrderDto;
  cartId: string;
}

export function DuplicateOrderConfirmDialog({
  order,
  cartId,
  isOpen,
  onClose,
  onSuccess,
}: Props) {
  const { handleShowMessage } = useAppContext();
  const orderIdToShow = order.id.substring(0, 8);

  const queryClient = useQueryClient();
  const updateMutation = useMutation(() =>
    setupFromExistingOrder(cartId, order.id),
  );

  const handleOnConfirm = async () => {
    //We close the modal regardless of success.
    onClose();

    try {
      const setupCartFromExistingOrderRes = await updateMutation.mutateAsync();
      queryClient.invalidateQueries([
        CartQueryKeys.findCartById,
        { id: cartId },
      ]);

      const wentNegativeOnAnyItem =
        setupCartFromExistingOrderRes.filter(
          (i) => i.code === 'NOT_ENOUGH_AVAILABLE',
        ).length > 0;
      const alertColor = wentNegativeOnAnyItem ? 'warning' : 'success';
      onSuccess?.(setupCartFromExistingOrderRes);
      handleShowMessage(
        `Items added to your cart from order '${order.id}': \n 
        ${setupCartFromExistingOrderRes.map((i) => `${i.msg}`).join('\n\n')}`,
        alertColor,
        20000,
      );
    } catch (err: any) {
      handleShowMessage(getErrorMessage(err), 'error');
    }
  };

  return (
    <ConfirmDialog
      title={`Duplicate Order: ${orderIdToShow}`}
      isOpen={isOpen}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={() => handleOnConfirm()}
    >
      <>
        <p>Are you sure you want to duplicate order '{orderIdToShow}'? </p>
        <p> Duplicating the order will:</p>
        <ul>
          <li>
            Remove all items from your cart. If you are working on placing
            another order, you should finish it before duplicating this order.
          </li>
          <li>Set your cart order week to be the current order week.</li>
          <li>
            Add all of the items from the order you are duplicating to your
            cart, even if there isn't enough inventory of an item.
          </li>
        </ul>
        <p>
          You will be notified if adding an item to your cart causes the
          available inventory for that item to go negative.
        </p>
      </>
    </ConfirmDialog>
  );
}
